import '@/shared/styles/tailwind.css';
import '@/shared/styles/global.scss';

import { useCallback, useEffect, useMemo } from 'react';
import { AppProps } from 'next/app';
import getConfig from 'next/config';
import { appWithTranslation, SSRConfig } from 'next-i18next';
import { Honeybadger } from '@honeybadger-io/react';

import { FrontlineAgentSurvey, SurveyProvider } from '@catawiki/survey';
import { captureException } from '@catawiki/observability';
import {
  createI18n,
  ErrorBoundary,
  ErrorPage,
  I18nInstance,
  I18nProvider,
  SnackbarContainer,
} from '@catawiki/cata-style';
import { ABSmartlyWrapper, Units } from '@catawiki/ab-test';
import { Assets, Cookie, Observability } from '@catawiki/runtime-toolkit';

import { getUserId } from '@/shared/helpers/cookies';
import { Layout } from '@/shared/components/Layout';
import { QueryClientWrapper } from '@/shared/components/QueryClientWrapper';
import { SentryProvider } from '@/shared/components/SentryProvider';
import { queryClient } from '@/shared/helpers/queryClient';
import { sellerProfileQueryParams } from '@/shared/queries/useSellerProfile';
import { useMobileApp } from '@/shared/hooks/useMobileApp';
import { isRelevantError } from '@/shared/utils/reporting';
import { APP_URL } from '@/shared/constants/routes';
import { WebVitalsTracker } from '@/shared/components/WebVitalsTracker/WebVitalsTracker';

interface SharedProps extends SSRConfig {
  assetsData?: Assets;
  locale?: string;
  err?: Error;
  units?: Units;
  isMobileApp?: boolean;
  showCookieBanner?: boolean;
}
const { publicRuntimeConfig } = getConfig();

// https://docs.honeybadger.io/lib/javascript/reference/configuration.html
Honeybadger.configure({
  apiKey: publicRuntimeConfig.honeybadgerApiKey,
  environment: publicRuntimeConfig.stage,
  revision: publicRuntimeConfig.lastCommitSha,
  projectRoot: 'webpack://_N_E/./',
  developmentEnvironments: ['development', 'staging', 'test'],
});

Honeybadger.setContext({
  user_id: getUserId(),
  platform: Cookie.getIsMobileApp() ? 'app' : 'web',
});

Honeybadger.beforeNotify(isRelevantError);

function MyApp({ Component, pageProps, router }: AppProps<SharedProps>) {
  const { _nextI18Next } = pageProps;
  const i18n = useMemo(() => createI18n(router.locale), []);
  const hideLayout = useMobileApp(pageProps.isMobileApp);
  const userId = pageProps.units?.userId ?? getUserId();

  useEffect(() => {
    if (typeof window !== 'undefined' && userId) {
      window.AB_USER_ID = String(userId);
      // To init Selligent SITE. The init script itself is loaded by GTM.
      window.SELLIGENT_USER_ID = String(userId);
    }

    queryClient.prefetchQuery(sellerProfileQueryParams);
  }, [userId]);

  const getTranslations = useCallback(() => {
    const initialI18nStore: Record<
      string,
      { translations: Record<string, string> }
    > = _nextI18Next?.initialI18nStore || {};

    return Object.entries(initialI18nStore).reduce<
      I18nInstance['translations']
    >((result, [loc, { translations }]) => {
      result[loc] = translations;
      return result;
    }, {});
  }, [_nextI18Next]);

  if (!pageProps?.assetsData || router.route === '/_error') {
    return <Component {...pageProps} err={pageProps.err} />;
  }

  const layout = (
    <Layout
      data={pageProps.assetsData}
      showCookieBanner={pageProps.showCookieBanner}
      hideFooter={hideLayout}
      hideHeader={hideLayout}
    >
      <Component {...pageProps} />
      <FrontlineAgentSurvey location="seller" />
      <SnackbarContainer />
      <WebVitalsTracker />
    </Layout>
  );

  return (
    <ErrorBoundary
      fallback={() => <ErrorPage withLogo appUrl={APP_URL} />}
      onError={(error, info) => {
        error.stack = info.componentStack || error.stack;
        captureException(error, {
          extra: {
            componentStack: info.componentStack,
          },
        });
        Observability.notifyHB(error, {
          fingerprint: 'app_error_boundary',
        });
      }}
    >
      <QueryClientWrapper
        devTools={publicRuntimeConfig.stage === 'development'}
      >
        <ABSmartlyWrapper
          sdkOptions={{
            endpoint: publicRuntimeConfig.abSmartlyEndpoint || 'ENDPOINT',
            apiKey: publicRuntimeConfig.abSmartlyApiKey || 'API_KEY',
            environment: publicRuntimeConfig.stage || 'STAGE',
          }}
          units={pageProps.units}
          locale={pageProps.locale || 'en'}
        >
          <SurveyProvider>
            <SentryProvider userId={userId} />
            <I18nProvider
              fallback={layout}
              value={i18n}
              onLoad={getTranslations}
            >
              {layout}
            </I18nProvider>
          </SurveyProvider>
        </ABSmartlyWrapper>
      </QueryClientWrapper>
    </ErrorBoundary>
  );
}

export default appWithTranslation(MyApp);
